import { QUESTS } from 'shared';
import type { ActiveQuest, QuestDefinition, QuestObjective } from 'shared';

export class QuestSystem {
  private activeQuests: Map<string, ActiveQuest> = new Map();
  private completedQuests: Set<string> = new Set();

  // Event callbacks
  public onQuestAccepted?: (quest: QuestDefinition) => void;
  public onQuestProgress?: (quest: ActiveQuest) => void;
  public onQuestComplete?: (quest: QuestDefinition) => void;
  public onQuestTurnedIn?: (quest: QuestDefinition) => void;

  constructor() {}

  public acceptQuest(questId: string): boolean {
    if (this.activeQuests.has(questId)) {
      console.log('Quest already active');
      return false;
    }

    if (this.completedQuests.has(questId)) {
      console.log('Quest already completed');
      return false;
    }

    const questDef = QUESTS[questId];
    if (!questDef) {
      console.warn(`Unknown quest: ${questId}`);
      return false;
    }

    // Check prerequisites
    if (questDef.prerequisiteQuestIds) {
      for (const prereqId of questDef.prerequisiteQuestIds) {
        if (!this.completedQuests.has(prereqId)) {
          console.log(`Prerequisite quest not complete: ${prereqId}`);
          return false;
        }
      }
    }

    // Create active quest with objectives
    const activeQuest: ActiveQuest = {
      questId,
      status: 'active',
      objectives: questDef.objectives.map((obj) => ({
        ...obj,
        current: 0,
      })),
      acceptedAt: Date.now(),
    };

    this.activeQuests.set(questId, activeQuest);
    this.onQuestAccepted?.(questDef);

    console.log(`📜 Quest accepted: ${questDef.name}`);
    return true;
  }

  public updateKillProgress(enemyType: string): void {
    this.activeQuests.forEach((quest, questId) => {
      if (quest.status !== 'active') return;

      let updated = false;
      quest.objectives.forEach((obj) => {
        if (obj.type === 'kill' && obj.targetId === enemyType && obj.current < obj.required) {
          obj.current++;
          updated = true;
          console.log(`Quest progress: ${obj.targetName} ${obj.current}/${obj.required}`);
        }
      });

      if (updated) {
        this.checkQuestCompletion(questId);
        this.onQuestProgress?.(quest);
      }
    });
  }

  public updateCollectProgress(itemId: string, count: number): void {
    this.activeQuests.forEach((quest, questId) => {
      if (quest.status !== 'active') return;

      let updated = false;
      quest.objectives.forEach((obj) => {
        if (obj.type === 'collect' && obj.targetId === itemId) {
          obj.current = count;
          updated = true;
        }
      });

      if (updated) {
        this.checkQuestCompletion(questId);
        this.onQuestProgress?.(quest);
      }
    });
  }

  private checkQuestCompletion(questId: string): void {
    const quest = this.activeQuests.get(questId);
    if (!quest || quest.status !== 'active') return;

    const allComplete = quest.objectives.every((obj) => obj.current >= obj.required);
    if (allComplete) {
      quest.status = 'complete';
      const questDef = QUESTS[questId];
      if (questDef) {
        console.log(`✅ Quest complete: ${questDef.name} - Return to turn in!`);
        this.onQuestComplete?.(questDef);
      }
    }
  }

  public turnInQuest(questId: string): { success: boolean; rewards?: { experience: number; gold: number; items?: { itemId: string; quantity: number }[] } } {
    const quest = this.activeQuests.get(questId);
    if (!quest || quest.status !== 'complete') {
      return { success: false };
    }

    const questDef = QUESTS[questId];
    if (!questDef) {
      return { success: false };
    }

    // Mark as turned in
    quest.status = 'turned_in';
    this.activeQuests.delete(questId);
    this.completedQuests.add(questId);

    this.onQuestTurnedIn?.(questDef);
    console.log(`🎉 Quest turned in: ${questDef.name}`);

    return {
      success: true,
      rewards: questDef.rewards,
    };
  }

  public getActiveQuests(): ActiveQuest[] {
    return Array.from(this.activeQuests.values());
  }

  public getQuest(questId: string): ActiveQuest | undefined {
    return this.activeQuests.get(questId);
  }

  public isQuestActive(questId: string): boolean {
    return this.activeQuests.has(questId);
  }

  public isQuestComplete(questId: string): boolean {
    const quest = this.activeQuests.get(questId);
    return quest?.status === 'complete';
  }

  public isQuestTurnedIn(questId: string): boolean {
    return this.completedQuests.has(questId);
  }

  public getAvailableQuests(npcId: string): QuestDefinition[] {
    const available: QuestDefinition[] = [];

    Object.values(QUESTS).forEach((quest) => {
      if (quest.giverNpcId !== npcId) return;
      if (this.activeQuests.has(quest.id)) return;
      if (this.completedQuests.has(quest.id)) return;

      // Check prerequisites
      if (quest.prerequisiteQuestIds) {
        const hasAllPrereqs = quest.prerequisiteQuestIds.every((id) =>
          this.completedQuests.has(id)
        );
        if (!hasAllPrereqs) return;
      }

      available.push(quest);
    });

    return available;
  }

  public getTurnInQuests(npcId: string): ActiveQuest[] {
    const turnIns: ActiveQuest[] = [];

    this.activeQuests.forEach((quest) => {
      const questDef = QUESTS[quest.questId];
      if (questDef && questDef.turnInNpcId === npcId && quest.status === 'complete') {
        turnIns.push(quest);
      }
    });

    return turnIns;
  }
}
